/**
 * Tax calculation utilities for services and addons
 */

/**
 * Determines if tax should be applied based on tax settings
 * @param {Object} taxSettings - The tax settings object
 * @returns {boolean} - Whether tax should be applied
 */
export const isTaxEnabled = (taxSettings) => {
  if (!taxSettings) return false;
  return taxSettings.isEnabled === true || taxSettings.isEnabled === 'true';
};

/**
 * Gets the tax rate from tax settings
 * @param {Object} taxSettings - The tax settings object
 * @returns {number} - The tax rate as a number
 */
export const getTaxRate = (taxSettings) => {
  if (!isTaxEnabled(taxSettings)) return 0;
  
  const rawRate = taxSettings.serviceRate || 0;
  return typeof rawRate === 'string' ? parseFloat(rawRate) : (typeof rawRate === 'number' ? rawRate : 0);
};

/**
 * Determines if addons should be included in tax calculations
 * @param {Object} taxSettings - The tax settings object
 * @returns {boolean} - Whether addons should be taxed
 */
export const shouldTaxAddons = (taxSettings) => {
  if (!isTaxEnabled(taxSettings)) return false;
  return taxSettings.includeAddons === true || taxSettings.includeAddons === 'true';
};

/**
 * Calculates tax amount based on price and tax rate
 * @param {number} price - The price to calculate tax on
 * @param {number} taxRate - The tax rate as a percentage
 * @returns {number} - The calculated tax amount
 */
export const calculateTaxAmount = (price, taxRate) => {
  if (!price || !taxRate) return 0;
  return price * (taxRate / 100);
};

/**
 * Calculates tax for a service including its addons
 * @param {Object} service - The service object
 * @param {Array} selectedAddons - Array of selected addons
 * @returns {Object} - Tax breakdown and totals
 */
export const calculateServiceTax = (service, selectedAddons = []) => {
  if (!service) return { taxAmount: 0, totalPrice: 0, breakdown: {} };
  
  const taxSettings = service.taxSettings || {};
  const isTaxActive = isTaxEnabled(taxSettings);
  const taxRate = getTaxRate(taxSettings);
  
  // Calculate base service price - use package price if a package is selected
  const baseServicePrice = service.selectedPackage?.price ? 
    parseFloat(service.selectedPackage.price) : 
    parseFloat(service.price) || 0;
  
  // Calculate addons subtotal
  const addonsSubtotal = selectedAddons.reduce((sum, addon) => sum + (parseFloat(addon.price) || 0), 0);
  
  // Calculate total price before tax
  const subtotal = baseServicePrice + addonsSubtotal;
  
  // Always use the full subtotal as the taxable amount
  const taxableAmount = subtotal;
  
  // Calculate tax amount
  const taxAmount = isTaxActive && taxRate > 0 ? calculateTaxAmount(taxableAmount, taxRate) : 0;
  
  // Calculate total price including tax
  const totalPrice = subtotal + taxAmount;
  
  return {
    taxAmount,
    totalPrice,
    breakdown: {
      basePrice: baseServicePrice,
      addonsPrice: addonsSubtotal,
      subtotal,
      taxableAmount,
      taxRate,
      taxAmount,
      total: totalPrice
    }
  };
};

/**
 * Calculates tax for an individual addon
 * @param {Object} addon - The addon object
 * @param {Object} taxSettings - The tax settings to apply
 * @returns {Object} - The addon with tax calculations
 */
export const calculateAddonTax = (addon, taxSettings) => {
  if (!addon) return null;
  
  const isTaxActive = isTaxEnabled(taxSettings);
  const shouldApplyTax = isTaxActive && shouldTaxAddons(taxSettings);
  const taxRate = shouldApplyTax ? getTaxRate(taxSettings) : 0;
  
  const addonPrice = parseFloat(addon.price) || 0;
  const taxAmount = shouldApplyTax ? calculateTaxAmount(addonPrice, taxRate) : 0;
  const totalPrice = addonPrice + taxAmount;
  
  return {
    ...addon,
    taxRate,
    taxAmount,
    totalPrice
  };
}; 